import { useMemo } from 'react';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Picks the menu products a bundle offers, in the order Contentful lists their
 * lineage keys. Products the restaurant doesn't sell are left out. When it
 * sells none of them, the products of categories whose name mentions plates
 * are used instead. An empty list offers nothing.
 */
export const useBundleProducts = <Product extends MenuProduct>(params: Params<Product>) => {
  const { categories, lineageKeys } = params;

  return useMemo(() => {
    if (!categories) {
      return [];
    }

    if (lineageKeys.length === 0) {
      return [];
    }

    const productsByLineageKey = getProductsByLineageKey(categories);
    const listedProducts = pickProductsByLineageKey(productsByLineageKey, lineageKeys);

    if (listedProducts.length > 0) {
      return listedProducts;
    }

    return categories.filter(checkIfFallbackCategory).flatMap(getCategoryProducts);
  }, [categories, lineageKeys]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getProductsByLineageKey<Product extends MenuProduct>(
  categories: readonly MenuCategory<Product>[],
) {
  const products = categories.flatMap(getCategoryProducts);
  const productsWithLineageKey = products.filter(checkIfHasLineageKey);

  return new Map(productsWithLineageKey.map(toLineageKeyEntry));
}

function pickProductsByLineageKey<Product>(
  productsByLineageKey: Map<string, Product>,
  lineageKeys: readonly string[],
) {
  const uniqueLineageKeys = [...new Set(lineageKeys)];

  return uniqueLineageKeys
    .map((lineageKey) => productsByLineageKey.get(lineageKey))
    .filter(checkIfDefined);
}

function getCategoryProducts<Product>(category: MenuCategory<Product>) {
  return category.products;
}

function checkIfHasLineageKey<Product extends MenuProduct>(
  product: Product,
): product is Product & ProductWithLineageKey {
  return Boolean(product.baseProduct.lineageKey);
}

function toLineageKeyEntry<Product extends ProductWithLineageKey>(product: Product) {
  return [product.baseProduct.lineageKey, product] as const;
}

function checkIfDefined<Value>(value: Value | undefined): value is Value {
  return value !== undefined;
}

function checkIfFallbackCategory<Product>(category: MenuCategory<Product>) {
  return category.name.toLowerCase().includes(FALLBACK_CATEGORY_KEYWORD);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FALLBACK_CATEGORY_KEYWORD = 'plates';

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuProduct = {
  baseProduct: { lineageKey: string | null };
};

type ProductWithLineageKey = {
  baseProduct: { lineageKey: string };
};

type MenuCategory<Product> = {
  name: string;
  products: readonly Product[];
};

type Params<Product> = {
  categories: readonly MenuCategory<Product>[] | undefined;
  lineageKeys: readonly string[];
};
